import React, { ReactElement, useState } from 'react';
import {
  Alert,
  Modal,
  Select,
  SelectOptionElement,
  Spinner,
} from '@odin-labs/components';
import { WrenchIcon } from 'components/icons';
import {
  GetScheduleDetailsDocument,
  GetSchedulesDocument,
  useGetScheduleNotificationPreviewQuery,
  useSendScheduleNotificationsMutation,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { ApolloError } from '@apollo/client';
import { getGraphQLError } from 'utils/error';
import { NotificationPreview } from './NotificationPreview';

export function ScheduleNotificationsModal(props: {
  isOpen: boolean;
  closeModal: () => void;
  onClosed?: () => void;
  refetchQuery: typeof GetSchedulesDocument | typeof GetScheduleDetailsDocument;
  scheduleId: string;
}): ReactElement {
  const {
    isOpen = false,
    closeModal,
    scheduleId,
    refetchQuery,
    onClosed,
  } = props;

  const [selectedPreviewOption, setSelectedPreviewOption] =
    useState<SelectOptionElement<number> | null>(null);

  const onAfterLeave = React.useCallback((): void => {
    // Reset the modal state
    setSelectedPreviewOption(null);
    onClosed?.();
  }, [onClosed]);

  const { data: previewQuery, loading: isDataLoading } =
    useGetScheduleNotificationPreviewQuery({
      variables: { scheduleId },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      skip: !scheduleId || !isOpen,
    });
  const allPreviews = previewQuery?.getScheduleNotificationPreview || [];

  // All previews that we have resolved email addresses for, thus can be sent.
  const deliverablePreviews = React.useMemo(
    () =>
      allPreviews.filter(
        (p) => Array.isArray(p?.to) && (p?.to?.length ?? 0) > 0,
      ),
    [allPreviews],
  );

  // All previews that are missing email address and cannot be delivered
  const undeliverablePreviews = React.useMemo(
    () =>
      allPreviews.filter(
        (p) => !Array.isArray(p?.to) || (p?.to?.length ?? 0) === 0,
      ),
    [allPreviews],
  );

  const [sendScheduleNotifications, { loading: isSaving }] =
    useSendScheduleNotificationsMutation({
      refetchQueries: [refetchQuery],
      variables: {
        input: {
          id: scheduleId,
        },
      },
      onCompleted: () => {
        AlertService.alert(
          'success',
          'Success',
          'Schedule notifications sent successfully',
        );
        closeModal();
      },
      onError: (error: ApolloError) => {
        AlertService.alert(
          'danger',
          'Something went wrong!',
          getGraphQLError(error),
        );
      },
    });

  const previewOptions: SelectOptionElement<number>[] = React.useMemo(() => {
    return deliverablePreviews.map((preview, index: number) => ({
      value: index,
      label: preview?.recipient ?? `Recipient ${index + 1}`,
    }));
  }, [deliverablePreviews]);

  React.useEffect(() => {
    // Initialize selection once when options first become available
    if (!selectedPreviewOption && previewOptions.length > 0) {
      setSelectedPreviewOption(previewOptions[0]);
    }
  }, [selectedPreviewOption, previewOptions]);

  const selectedPreview = React.useMemo(
    () => deliverablePreviews?.[selectedPreviewOption?.value],
    [deliverablePreviews, selectedPreviewOption],
  );

  return (
    <Modal
      actionButtonEnabled={!isDataLoading && deliverablePreviews.length > 0}
      actionButtonTheme="primary"
      actionText="Send notifications"
      actionButtonWithSpinner={isSaving}
      cancelText="Cancel"
      onAction={() => {
        void sendScheduleNotifications();
      }}
      afterLeave={onAfterLeave}
      open={isOpen}
      setOpen={closeModal}
      title="Send schedule notifications"
      size="md"
    >
      {isDataLoading ? (
        <div className="odin-flex odin-items-center odin-justify-center">
          <Spinner size="base" className="odin-text-odin-primary" />
        </div>
      ) : (
        <div className="odin-flex odin-flex-col odin-gap-4">
          {undeliverablePreviews.length > 0 && (
            <Alert
              text={
                <div>
                  <p className="odin-m-0">
                    The following organizations will not receive notifications
                    because they do not have an email address configured in
                    Odin:
                  </p>
                  <ul className="odin-list-disc odin-pl-5 odin-mt-2 odin-space-y-1">
                    {undeliverablePreviews
                      .map((p) => p?.recipient || 'Unknown Org')
                      .filter(Boolean)
                      .map((recipient) => (
                        <li key={recipient}>{recipient}</li>
                      ))}
                  </ul>
                </div>
              }
            />
          )}
          <Select
            label="Notification recipient"
            value={selectedPreviewOption}
            name="notificationToPreview"
            icon={WrenchIcon}
            options={previewOptions}
            onChange={setSelectedPreviewOption}
            isClearable={false}
          />

          {selectedPreview && (
            <>
              <p>
                <b>Subject:</b> {selectedPreview.subject}
              </p>
              {Array.isArray(selectedPreview.to) &&
                selectedPreview.to.length > 0 && (
                  <p>
                    <b>To:</b> {selectedPreview.to.join('; ')}
                  </p>
                )}
              <div className="odin-flex odin-flex-col odin-gap-2 odin-border odin-border-gray-200 odin-rounded-lg odin-min-h-[200px] odin-h-[50vh] odin-overflow-auto">
                <NotificationPreview body={selectedPreview.body} />
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
